import { ApiError, del, patch, post } from '../api/client';
import type { WakeMode } from '../api/types';
import { isMissing } from '../api/queries';
import { toast, toastErr } from '../components/toast';
import { t } from '../i18n';
import { useLive } from '../live/store';

/** Current autonomy state of a robot: status.autonomy (server truth) refined by the latest /ws/ui "state" event. */
export function useAutonomy(serial: string) {
  const st = useLive((s) => s.robots[serial]?.status?.autonomy);
  const live = useLive((s) => s.autonomyState[serial]);
  const active = Boolean(st?.active) && live !== 'ended';
  const state = (active ? (live && live !== 'ended' ? live : st?.state) : null) ?? (active ? 'idle' : null);
  return { active, state: state as string | null, profileId: st?.profileId ?? null, wake: st?.wake ?? null };
}

export async function startAutonomy(serial: string, profileId: string, wake?: WakeMode | '') {
  try {
    await post(`/api/robots/${encodeURIComponent(serial)}/autonomy`, { profileId, ...(wake ? { wake } : {}) });
    return true;
  } catch (e) {
    if (e instanceof ApiError && isMissing(e)) toast(t('auto.apiMissing'), 'err');
    else toastErr(e);
    return false;
  }
}
/** Change the microphone mode of a running autonomy session. */
export async function setAutonomyWake(serial: string, wake: WakeMode) {
  try {
    await patch(`/api/robots/${encodeURIComponent(serial)}/autonomy`, { wake });
    return true;
  } catch (e) {
    if (e instanceof ApiError && isMissing(e)) toast(t('auto.wakeApiMissing'), 'err');
    else toastErr(e);
    return false;
  }
}

export async function stopAutonomy(serial: string) {
  try {
    await del(`/api/robots/${encodeURIComponent(serial)}/autonomy`);
    return true;
  } catch (e) {
    if (e instanceof ApiError && isMissing(e)) toast(t('auto.apiMissing'), 'err');
    else toastErr(e);
    return false;
  }
}


/** Operator types as the visitor: the server treats it like a heard utterance (engine "web"); the bubble arrives via /ws/ui. */
export async function sendAutonomyUtterance(serial: string, text: string, lang: string) {
  try {
    await post(`/api/robots/${encodeURIComponent(serial)}/autonomy/utterance`, { text, lang });
    return true;
  } catch (e) {
    if (e instanceof ApiError && isMissing(e)) toast(t('auto.typeApiMissing'), 'err');
    else toastErr(e);
    return false;
  }
}

/** Live session parameters while autonomy runs (do not pause it): `PATCH …/autonomy {maxSpeed?, turnSpeed?, lang?}`. */
export async function setAutonomyParams(serial: string, patchBody: { maxSpeed?: number; turnSpeed?: number; lang?: string }) {
  try {
    await patch(`/api/robots/${encodeURIComponent(serial)}/autonomy`, patchBody);
    return true;
  } catch (e) {
    if (e instanceof ApiError && isMissing(e)) toast(t('auto.paramsApiMissing'), 'err');
    else toastErr(e);
    return false;
  }
}
